import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "../../firebase";
import { listMyLicenses, type LicenseDoc, formatExpiry } from "../../api/functions";

/**
 * Home page for customers. Lists every license they own as a tile, each
 * subscribed live to its `licenses/{id}/reports/today` doc — owners see
 * today's sales-at-a-glance across all their restaurants. Clicking a tile
 * opens the full owner dashboard.
 *
 * No PIN gate — Firebase Auth + Firestore rules are the access control.
 */
export function ReportsPage() {
  const [licenses, setLicenses] = useState<LicenseDoc[] | null>(null);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    listMyLicenses()
      .then(r => setLicenses(r.licenses))
      .catch(e => setError(e?.message ?? "Failed to load licenses"));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Today</h1>
        <p className="text-slate-500 text-sm mt-1">
          Live sales across your restaurants. Tap a card for the full dashboard.
        </p>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {licenses === null && !error && <div className="text-slate-500">Loading…</div>}

      {licenses && licenses.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <div className="text-4xl mb-3">🍽️</div>
          <div className="font-semibold text-slate-800">No restaurants linked yet</div>
          <div className="text-sm text-slate-500 mt-2">
            Reports will appear here once your first license is activated on a POS.
          </div>
        </div>
      )}

      {licenses && licenses.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {licenses.map(l => <RestaurantCard key={l.docId} license={l} />)}
        </div>
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────

interface ReportSnapshot {
  totalSale?: number;
  netSales?: number;
  totalBills?: number;
  lastSync?: string;
}

// Six restaurant-warm accent palettes. A card picks one deterministically from
// its restaurantId so the colour stays stable across reloads but adjacent
// cards in the grid look distinct from each other.
const ACCENTS = [
  { bar: "bg-rose-500",    grad: "from-rose-50 to-pink-50",       border: "border-rose-100",    amount: "text-rose-700",    hover: "hover:border-rose-300" },
  { bar: "bg-emerald-500", grad: "from-emerald-50 to-teal-50",    border: "border-emerald-100", amount: "text-emerald-700", hover: "hover:border-emerald-300" },
  { bar: "bg-amber-500",   grad: "from-amber-50 to-orange-50",    border: "border-amber-100",   amount: "text-amber-700",   hover: "hover:border-amber-300" },
  { bar: "bg-indigo-500",  grad: "from-indigo-50 to-violet-50",   border: "border-indigo-100",  amount: "text-indigo-700",  hover: "hover:border-indigo-300" },
  { bar: "bg-cyan-500",    grad: "from-cyan-50 to-sky-50",        border: "border-cyan-100",    amount: "text-cyan-700",    hover: "hover:border-cyan-300" },
  { bar: "bg-fuchsia-500", grad: "from-fuchsia-50 to-purple-50",  border: "border-fuchsia-100", amount: "text-fuchsia-700", hover: "hover:border-fuchsia-300" },
] as const;

function pickAccent(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  return ACCENTS[Math.abs(h) % ACCENTS.length];
}

function RestaurantCard({ license }: { license: LicenseDoc }) {
  const activated = !!license.machineId;
  const docId     = license.docId ?? "";
  const accent    = pickAccent(license.restaurantId || license.docId || license.restaurantName || "x");

  const [snap, setSnap]       = useState<ReportSnapshot | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);

  useEffect(() => {
    if (!activated || !docId) return;
    const ref = doc(db, "licenses", docId, "reports", "today");
    let unsub: Unsubscribe | undefined;
    try {
      unsub = onSnapshot(
        ref,
        s => {
          if (!s.exists()) {
            setHasData(false);
            setSnap(null);
            return;
          }
          setHasData(true);
          setSnap(s.data() as ReportSnapshot);
        },
        () => { setHasData(false); },
      );
    } catch {
      setHasData(false);
    }
    return () => { unsub?.(); };
  }, [docId, activated]);

  const totalSale = snap?.totalSale ?? snap?.netSales ?? 0;
  const bills     = snap?.totalBills ?? 0;
  const lastSync  = useMemo(() => {
    if (!snap?.lastSync) return null;
    const d = new Date(snap.lastSync);
    return isNaN(d.getTime())
      ? null
      : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }, [snap?.lastSync]);

  if (!activated) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 opacity-80 overflow-hidden relative">
        <div className="absolute inset-x-0 top-0 h-1 bg-slate-300"/>
        <CardHeader name={license.restaurantName} restaurantId={license.restaurantId} status={license.status} />
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Activate this license on a POS to see today's reports here.
        </div>
        <div className="text-xs text-slate-500 mt-3">
          Expires {formatExpiry(license.expiry)}
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/reports/${docId}`}
      className={"group block bg-white rounded-xl border border-slate-200 hover:shadow-lg transition p-5 overflow-hidden relative " + accent.hover}
    >
      {/* Top accent stripe — picks each card out at a glance in a chain */}
      <div className={"absolute inset-x-0 top-0 h-1 " + accent.bar}/>

      <CardHeader name={license.restaurantName} restaurantId={license.restaurantId} status={license.status} />

      {/* Headline numbers — gradient + amount in the card's accent colour */}
      <div className={"mt-4 bg-gradient-to-br " + accent.grad + " border " + accent.border + " rounded-lg p-4"}>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          Today's Sale
        </div>
        <div className="flex items-baseline justify-between mt-1.5 gap-3">
          <div className={"text-2xl font-extrabold tabular-nums leading-none " + accent.amount}>
            {hasData === null ? "…" : hasData === false ? "—" : fmtINR(totalSale)}
          </div>
          <div className="text-right shrink-0">
            <div className="text-lg font-bold text-slate-700 tabular-nums leading-none">
              {hasData === null ? "…" : bills}
            </div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
              {bills === 1 ? "bill" : "bills"}
            </div>
          </div>
        </div>
        {hasData === false && (
          <div className="text-xs text-slate-600 mt-3">No bills settled today yet.</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <span>{lastSync ? `Synced ${lastSync}` : `Expires ${formatExpiry(license.expiry)}`}</span>
        <span className={"font-medium group-hover:underline " + accent.amount}>Open →</span>
      </div>
    </Link>
  );
}

function CardHeader({ name, restaurantId, status }: { name: string; restaurantId: string; status: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-bold text-slate-800 text-base leading-tight truncate">
          {name || "Unnamed"}
        </div>
        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
          {restaurantId || "—"}
        </div>
      </div>
      <Status status={status} />
    </div>
  );
}

function Status({ status }: { status: string }) {
  const styles =
    status === "active"    ? "bg-emerald-100 text-emerald-800" :
    status === "suspended" ? "bg-amber-100 text-amber-800" :
                             "bg-slate-200 text-slate-700";
  return (
    <span className={"shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider " + styles}>
      {status}
    </span>
  );
}

const fmtINR = (n: number) =>
  "₹" + Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
